import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          setTimeout(() => navigate("/portal/billing"), 2500);
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries > 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0f17] p-6" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-[#111827] p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-500" />
            <h1 className="mt-4 font-display text-xl font-bold text-white">Confirming your subscription…</h1>
            <p className="mt-2 text-sm text-slate-400">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h1 className="mt-4 font-display text-xl font-bold text-white">You're subscribed!</h1>
            <p className="mt-2 text-sm text-slate-400">Taking you back to billing…</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h1 className="mt-4 font-display text-xl font-bold text-white">We couldn't confirm the payment</h1>
            <p className="mt-2 text-sm text-slate-400">If you completed checkout, your plan will update shortly.</p>
            <Link to="/portal/billing" data-testid="back-to-billing"
              className="mt-5 inline-block rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">
              Back to billing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
